import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';

export const SEARCH_DEBOUNCE_MS = 500;
export const SEARCH_MIN_CHARS = 3;

export function normalizeSearchValue(value: string): string {
  return value.trim();
}

/** Automatic search is allowed for empty (clear/reset) or values meeting the minimum length. */
export function canAutoSubmitSearch(value: string, minChars: number = SEARCH_MIN_CHARS): boolean {
  const trimmed = normalizeSearchValue(value);
  return trimmed.length === 0 || trimmed.length >= minChars;
}

export interface UseDebouncedSearchOptions {
  /** Debounce delay after the latest keystroke. Default 500ms. */
  delayMs?: number;
  /** Minimum characters required before automatic search. Default 3. Empty always auto-submits. */
  minChars?: number;
  /** Called only when a new value is actually committed (not on duplicate or initial mount). */
  onCommit?: (value: string) => void;
}

export interface DebouncedSearch {
  /** Live controlled input value. Bind this to the text field. */
  inputValue: string;
  /** Last committed search value. Use this in query keys and API requests. */
  submittedValue: string;
  setInputValue: (value: string) => void;
  /** Immediate commit (Enter). Cancels any pending debounce. */
  submitNow: (value?: string) => void;
  /** Clear the input and restore the unfiltered list with a single request. */
  clear: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * Shared text-search behaviour for ERP list/filter inputs:
 * - 1–2 characters: no automatic API
 * - 3+ characters: 500ms debounce, reset on every keystroke
 * - Enter: immediate search at any length, cancels pending debounce
 * - Duplicate commits for the same value are skipped
 * - Clearing the input commits empty immediately (one restore request)
 */
export function useDebouncedSearch(
  initialValue = '',
  options: UseDebouncedSearchOptions = {}
): DebouncedSearch {
  const delayMs = options.delayMs ?? SEARCH_DEBOUNCE_MS;
  const minChars = options.minChars ?? SEARCH_MIN_CHARS;

  const [inputValue, setInputValueState] = useState(initialValue);
  const [submittedValue, setSubmittedValue] = useState(() => normalizeSearchValue(initialValue));

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSubmittedRef = useRef(normalizeSearchValue(initialValue));
  const inputValueRef = useRef(initialValue);
  const onCommitRef = useRef(options.onCommit);
  onCommitRef.current = options.onCommit;

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const commit = useCallback(
    (raw: string) => {
      const next = normalizeSearchValue(raw);
      clearTimer();
      if (next === lastSubmittedRef.current) {
        return;
      }
      lastSubmittedRef.current = next;
      setSubmittedValue(next);
      onCommitRef.current?.(next);
    },
    [clearTimer]
  );

  const setInputValue = useCallback(
    (value: string) => {
      setInputValueState(value);
      inputValueRef.current = value;
      const trimmed = normalizeSearchValue(value);

      if (trimmed.length === 0) {
        commit('');
        return;
      }

      clearTimer();

      if (trimmed.length < minChars) {
        return;
      }

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        commit(inputValueRef.current);
      }, delayMs);
    },
    [clearTimer, commit, delayMs, minChars]
  );

  const submitNow = useCallback(
    (value?: string) => {
      if (value !== undefined) {
        setInputValueState(value);
        inputValueRef.current = value;
      }
      commit(value ?? inputValueRef.current);
    },
    [commit]
  );

  const clear = useCallback(() => {
    setInputValueState('');
    inputValueRef.current = '';
    commit('');
  }, [commit]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        submitNow();
      }
    },
    [submitNow]
  );

  useEffect(() => () => clearTimer(), [clearTimer]);

  return {
    inputValue,
    submittedValue,
    setInputValue,
    submitNow,
    clear,
    onKeyDown,
  };
}
